/* eslint-disable @typescript-eslint/no-explicit-any */

interface RequestOptions extends RequestInit {
  headers?: HeadersInit;
  body?: any;
  responseType?: 'json' | 'blob' | 'text';
  retries?: number;
  retryDelay?: number;
  uid?: string;
  idToken?: string;
}

const isBrowser = typeof window !== 'undefined';

const isOnline = (): boolean => {
  return isBrowser ? navigator.onLine : true;
};

class ApiClient {
  private static instance: ApiClient;
  private baseURL: string;
  private idToken: string | null = null;
  private pendingRequests: Map<string, Promise<any>> = new Map();

  constructor(baseURL: string, idToken?: string) {
    this.baseURL = baseURL;
    if (idToken) {
      this.idToken = idToken;
    }

    if (isBrowser) {
      window.addEventListener('online', this.handleNetworkChange);
      window.addEventListener('offline', this.handleNetworkChange);
    }
  }

  private handleNetworkChange = () => {
    if (!isBrowser) return;

    this.pendingRequests.clear();

    if (navigator.onLine) {
      this.dispatchEvent('network:online');
    } else {
      this.dispatchEvent('network:offline');
    }
  };

  private dispatchEvent(eventName: string, detail?: any) {
    if (!isBrowser) return;

    const event = new CustomEvent(eventName, { detail });
    window.dispatchEvent(event);
  }

  static getInstance(baseURL: string, idToken?: string): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient(baseURL, idToken);
    }
    return ApiClient.instance;
  }

  setApiClientIdToken(token: string | null) {
    this.idToken = token;
  }

  private async request<T>(method: string, url: string, options: RequestOptions = {}): Promise<T> {
    const {
      headers,
      body,
      responseType = 'json',
      retries = 2,
      retryDelay = 1000,
      idToken,
      ...rest
    } = options;

    const requestKey = `${method}:${url}:${JSON.stringify(body || {})}`;

    if (isBrowser && !isOnline()) {
      const errorMsg = 'You are offline. Please check your connection.';
      return Promise.reject(new Error(errorMsg));
    }

    if (this.pendingRequests.has(requestKey)) {
      return this.pendingRequests.get(requestKey) as Promise<T>;
    }

    const token = idToken || this.idToken;
    const config: RequestInit = {
      method,
      headers: Object.fromEntries(
        Object.entries({
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...headers
        }).filter(([, value]) => value !== null)
      ),
      mode: 'cors',
      ...rest
    };

    if (body) {
      if (body instanceof FormData) {
        config.body = body;
        config.headers = {
          ...config.headers
        };
        // Let the browser set the boundary.
        delete (config.headers as any)['Content-Type'];
      } else if (typeof body === 'object') {
        config.headers = {
          ...config.headers
        };
        config.body = JSON.stringify(body);
      } else {
        config.body = body;
      }
    }

    const makeRequest = async (attempt: number = 0): Promise<T> => {
      try {
        const response = await fetch(`${this.baseURL}${url}`, config);

        if (response.status === 204) {
          return {} as T;
        }

        const contentType = response.headers.get('content-type');
        let data: T;

        switch (responseType) {
          case 'blob':
            data = (await response.blob()) as unknown as T;
            break;
          case 'text':
            data = (await response.text()) as unknown as T;
            break;
          case 'json':
          default:
            if (contentType?.includes('application/json')) {
              data = await response.json();
            } else {
              data = (await response.text()) as unknown as T;
            }
            break;
        }

        if (!response.ok) {
          return Promise.reject({ status: response.status, data });
        }

        return data;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);

        const isNetworkError =
          (isBrowser && !isOnline()) ||
          errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('network') ||
          errorMessage.includes('net::ERR_NAME_NOT_RESOLVED') ||
          errorMessage.includes('Network') ||
          errorMessage.includes('ERR_NETWORK') ||
          errorMessage.includes('ERR_NETWORK_CHANGED') ||
          errorMessage.includes('connection') ||
          errorMessage.includes('Connection');

        if (isNetworkError && attempt < retries) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          return makeRequest(attempt + 1);
        }

        return Promise.reject(error);
      }
    };

    const requestPromise = makeRequest().finally(() => {
      this.pendingRequests.delete(requestKey);
    });

    // this.pendingRequests.set(requestKey, requestPromise);

    return requestPromise;
  }

  get<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', url, options);
  }

  post<T>(url: string, body: any, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('POST', url, { ...options, body });
  }

  put<T>(url: string, body: any, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('PUT', url, { ...options, body });
  }

  delete<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', url, options);
  }

  patch<T>(url: string, body: any, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('PATCH', url, { ...options, body });
  }

  async stream(
    method: string,
    url: string,
    body: any,
    options: RequestOptions = {}
  ): Promise<Response> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- body omitted from rest
    const { headers, idToken, body: _body, ...rest } = options;

    if (isBrowser && !isOnline()) {
      const errorMsg = 'You are offline. Please check your connection.';
      return Promise.reject(new Error(errorMsg));
    }

    const token = idToken || this.idToken;
    const config: RequestInit = {
      method,
      headers: Object.fromEntries(
        Object.entries({
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...headers
        }).filter(([, value]) => value !== null)
      ),
      mode: 'cors',
      ...rest
    };

    if (body) {
      if (body instanceof FormData) {
        config.body = body;
        config.headers = {
          ...config.headers
        };
        delete (config.headers as any)['Content-Type'];
      } else if (typeof body === 'object') {
        config.body = JSON.stringify(body);
      } else {
        config.body = body;
      }
    }

    const response = await fetch(`${this.baseURL}${url}`, config);

    if (!response.ok) {
      let errorMessage = `Request failed with status ${response.status}`;
      try {
        const errorText = await response.text();
        if (errorText) {
          try {
            const parsed = JSON.parse(errorText);
            errorMessage =
              (parsed as { error?: string; message?: string }).error ||
              parsed.message ||
              errorMessage;
          } catch {
            errorMessage = errorText;
          }
        }
      } catch {
        // ignore secondary parsing errors
      }
      throw new Error(errorMessage);
    }

    return response;
  }

  streamPost(url: string, body: any, options: RequestOptions = {}) {
    return this.stream('POST', url, body, options);
  }
}

const getApiUrl = () => {
  if (import.meta.env.VITE_CORE_API_BASE_URL) {
    return import.meta.env.VITE_CORE_API_BASE_URL;
  }

  return 'http://localhost:8080';
};

export const apiClient = ApiClient.getInstance(getApiUrl());

export const setApiClientIdToken = (token: string | null) => {
  apiClient.setApiClientIdToken(token);
};

export type { RequestOptions };
export default ApiClient;
